import { RefObject, useEffect } from 'react';

import {
  Middleware,
  Placement,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';

type PositionFloatingElementOptions = {
  anchorElement: HTMLElement | { getBoundingClientRect: () => DOMRect };
  floatingElement: HTMLElement;
  placement?: Placement;
  middleware?: Middleware[];
};

export const positionFloatingElement = async ({
  anchorElement,
  floatingElement,
  placement = 'bottom-start',
  middleware = [offset(8), flip(), shift({ padding: 8 })],
}: PositionFloatingElementOptions): Promise<void> => {
  const { x, y } = await computePosition(anchorElement, floatingElement, {
    placement,
    middleware,
  });

  Object.assign(floatingElement.style, {
    left: `${x}px`,
    top: `${y}px`,
  });
};

type UseFloatingPositionOptions = {
  isOpen: boolean;
  anchorElement: HTMLElement | { getBoundingClientRect: () => DOMRect } | null;
  floatingRef: RefObject<HTMLElement>;
  placement?: Placement;
  middleware?: Middleware[];
};

export const useFloatingPosition = ({
  isOpen,
  anchorElement,
  floatingRef,
  placement = 'bottom-start',
  middleware = [offset(8), flip(), shift({ padding: 8 })],
}: UseFloatingPositionOptions) => {
  useEffect(() => {
    const updatePosition = async () => {
      if (!anchorElement || !floatingRef.current || !isOpen) return;

      await positionFloatingElement({
        anchorElement,
        floatingElement: floatingRef.current,
        placement,
        middleware,
      });
    };

    updatePosition();
  }, [isOpen, anchorElement, placement, middleware, floatingRef]);
};
